/**
 * Helpers for the user's home location ("ort").
 *
 * The ort is picked with FieldLocationAutocompleteInput at signup (and on
 * ProfileSettingsPage) and stored in the user's protectedData as a plain
 * JSON object — SDK types (LatLng/LatLngBounds) can't be stored in
 * extended data directly:
 *
 *   protectedData.homeLocation = {
 *     address: 'Göteborg, Sverige',
 *     origin: { lat: 57.70887, lng: 11.97456 },
 *     bounds: { ne: { lat, lng }, sw: { lat, lng } },   // optional
 *   }
 *
 * These helpers convert between that stored shape, the autocomplete form
 * value shape ({ search, selectedPlace }), and the SDK types the map and
 * the `origin` search param need.
 */
import { types as sdkTypes } from './sdkLoader';

const { LatLng, LatLngBounds } = sdkTypes;

/**
 * FieldLocationAutocompleteInput form value -> plain object for extended data.
 * Returns null unless a place has actually been selected.
 */
export const serializeHomeLocation = locationFieldValue => {
  const { address, origin, bounds } = locationFieldValue?.selectedPlace || {};
  if (!address || origin?.lat == null || origin?.lng == null) {
    return null;
  }
  const boundsMaybe =
    bounds?.ne && bounds?.sw
      ? {
          bounds: {
            ne: { lat: bounds.ne.lat, lng: bounds.ne.lng },
            sw: { lat: bounds.sw.lat, lng: bounds.sw.lng },
          },
        }
      : {};
  return { address, origin: { lat: origin.lat, lng: origin.lng }, ...boundsMaybe };
};

/**
 * Stored homeLocation -> initial value for FieldLocationAutocompleteInput.
 */
export const homeLocationToFormValue = homeLocation => {
  const { address, origin, bounds } = homeLocation || {};
  if (!address || origin?.lat == null || origin?.lng == null) {
    return null;
  }
  const boundsMaybe =
    bounds?.ne && bounds?.sw
      ? {
          bounds: new LatLngBounds(
            new LatLng(bounds.ne.lat, bounds.ne.lng),
            new LatLng(bounds.sw.lat, bounds.sw.lng)
          ),
        }
      : {};
  return {
    search: address,
    predictions: [],
    selectedPlace: { address, origin: new LatLng(origin.lat, origin.lng), ...boundsMaybe },
  };
};

/**
 * Stored homeLocation -> LatLng for map centering, or null.
 */
export const homeLocationToOrigin = homeLocation => {
  const { origin } = homeLocation || {};
  return origin?.lat != null && origin?.lng != null ? new LatLng(origin.lat, origin.lng) : null;
};

/**
 * Stored homeLocation -> LatLngBounds for map centering, or null.
 */
export const homeLocationToBounds = homeLocation => {
  const { bounds } = homeLocation || {};
  return bounds?.ne && bounds?.sw
    ? new LatLngBounds(
        new LatLng(bounds.ne.lat, bounds.ne.lng),
        new LatLng(bounds.sw.lat, bounds.sw.lng)
      )
    : null;
};

/**
 * Stored homeLocation -> `origin` search param value ("lat,lng"), or null.
 * Same format the "Nära mig" sort pushes to the URL.
 */
export const homeLocationToOriginParam = homeLocation => {
  const { origin } = homeLocation || {};
  return origin?.lat != null && origin?.lng != null
    ? `${origin.lat.toFixed(5)},${origin.lng.toFixed(5)}`
    : null;
};

/**
 * Convenience: read the stored homeLocation from a currentUser entity.
 */
export const currentUserHomeLocation = currentUser =>
  currentUser?.attributes?.profile?.protectedData?.homeLocation || null;

/**
 * Decide what to write to `protectedData.homeLocation` when a profile form
 * is saved. Returns the fragment to spread into `protectedData`:
 *
 *   - valid selected place        -> { homeLocation: <serialized object> }
 *   - field cleared on purpose     -> { homeLocation: null }   (erase it)
 *   - untouched / partial text     -> {}                       (keep stored)
 *
 * "Cleared on purpose" means the autocomplete field has no typed text and no
 * selected place. We only erase when something was actually stored, so a
 * plain profile save by a user who never set an ort writes nothing. An
 * untouched field that still holds the prefilled value serializes normally
 * and takes the first branch. Sending `null` removes the key in Sharetribe
 * extended data, which is the point for data-erasure (GDPR).
 */
export const resolveHomeLocationUpdate = (locationFieldValue, storedHomeLocation) => {
  const serialized = serializeHomeLocation(locationFieldValue);
  if (serialized) {
    return { homeLocation: serialized };
  }
  const fieldCleared = !locationFieldValue?.search && !locationFieldValue?.selectedPlace?.address;
  if (fieldCleared && storedHomeLocation) {
    return { homeLocation: null };
  }
  return {};
};
